import type { ComponentProps, ReactNode, Ref } from 'react';
import { Popover as Base } from '@base-ui/react/popover';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';

type Focus = Pick<ComponentProps<typeof Base.Popup>, 'initialFocus' | 'finalFocus'>;

/** A popover is a dialog: it is named by its visible `title`, or by `label` when it has none (P11 a11y audit). */
function Popup({
  title,
  label,
  children,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  className,
  initialFocus,
  finalFocus,
  anchor,
  ref,
}: {
  title?: ReactNode;
  label?: string;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  className?: string;
  /** Positions it against this instead of its trigger (a comment pin, a point: `pointAnchor`). */
  anchor?: ComponentProps<typeof Base.Positioner>['anchor'];
  ref?: Ref<HTMLDivElement>;
} & Focus) {
  return (
    <Base.Portal>
      <Base.Positioner side={side} align={align} sideOffset={sideOffset} anchor={anchor} className="z-50" {...{ [CANVAS_UI]: '' }}>
        <Base.Popup ref={ref} aria-label={title ? undefined : label} initialFocus={initialFocus} finalFocus={finalFocus} className={cn('os-pop max-w-[calc(100vw-2rem)] rounded-2xl bg-chrome p-4 font-sans text-sm text-fg shadow-float outline-none', className)}>
          {title && <Base.Title className="mb-2 text-xs font-medium text-fg-muted">{title}</Base.Title>}
          {children}
        </Base.Popup>
      </Base.Positioner>
    </Base.Portal>
  );
}

export const Popover = { Root: Base.Root, Trigger: Base.Trigger, Close: Base.Close, Popup };
